import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { LoginService } from '@ebw-monorepo/login';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { EbwModalService } from '@ebw-monorepo/modal';
import Feature from 'ol/Feature';
import { Geometry, Polygon, SimpleGeometry } from 'ol/geom';
import Draw from 'ol/interaction/Draw.js';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { BehaviorSubject, catchError, Observable, throwError } from 'rxjs';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { PlotMapSnackbarComponent } from '../components/plot-map/plot-map-snackbar/plot-map-snackbar.component';
import { PlotMapComponent } from '../components/plot-map/plot-map.component';
import { MapService } from './map.service';
import { PinnedSnackbar, SnackbarService } from './snackbar.service';

/**
 * Service for handling the plotting tool
 */
@Injectable({
  providedIn: 'root'
})
export class PlotService {
  // reference to the dialog menu
  private _dialogRef: MatDialogRef<PlotMapComponent>;
  // reference to the snackbar menu
  private _snackbarRef: PinnedSnackbar;
  // vector layer and source where to draw the selection area

  private _temporaryDrawingLayer: VectorLayer<Feature<Geometry>>;
  private _temporaryDrawingSource: VectorSource;
  // drawing object for drawing the selection area
  private _drawObject: Draw;

  // BehaviorSubject per tracciare lo stato di disegno
  private _isLayerDrawnSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLayerDrawn$: Observable<boolean> = this._isLayerDrawnSubject.asObservable();

  private static readonly CLICK_OWNER_ID = 'plot';

  constructor(
    private _dialog: MatDialog,
    private _snackbar: SnackbarService,
    private _mapService: MapService,
    private _ebwModalSrv: EbwModalService,
    private _http: HttpClient,
    private _loginService: LoginService,
    private _i18nService: I18NService,
    private _handler: HttpBackend,
    private _apiUrlService: ApiUrlService
  ) {}

  /**
   * method to open the plotting tool
   * @returns
   */
  public openPlot() {
    // if the templates aren't ready, show a warning message to the user explaining the situation
    if (!sessionStorage.getItem('templates')) {
      this._ebwModalSrv.openWarningModal({
        title: this._i18nService.getInstantValue('common.warning'),
        content: this._i18nService.getInstantValue('map.plotting.templates_loading')
      });
      return;
    }

    // il plot è un tool "strong" del click sulla mappa: se c'è già uno strumento aperto il registro mostra l'errore
    if (!this._mapService.acquireMapClick({ id: PlotService.CLICK_OWNER_ID, level: 'strong' })) {
      return;
    }

    // create the vector layer and source objects for the drawing
    this._temporaryDrawingSource = new VectorSource({ wrapX: false });
    this._temporaryDrawingLayer = new VectorLayer({ source: this._temporaryDrawingSource });

    // add the layer to the OpenLayers map
    this._mapService.openLayersMap.addLayer(this._temporaryDrawingLayer);
    // set the z-index so that it's always rendered first
    this._temporaryDrawingLayer.setZIndex(9999);

    // create the dialog window
    this._dialogRef = this._dialog.open(PlotMapComponent, {
      minWidth: '80vw',
      disableClose: true,
      hasBackdrop: true
    });

    // routine to execute after the tool is closed, cleaning all the variables and the map
    this._dialogRef.afterClosed().subscribe((_: any) => {
      this._mapService.openLayersMap.removeLayer(this._temporaryDrawingLayer);
      this._mapService.openLayersMap.removeInteraction(this._drawObject);
      this._temporaryDrawingSource.clear();
      this._temporaryDrawingSource = null;
      this._temporaryDrawingLayer = null;
      this._drawObject = null;
      this._dialogRef = null;
      this._snackbarRef?.dismiss();
      this._snackbarRef = null;

      // Aggiorna lo stato di disegno
      this._isLayerDrawnSubject.next(false);

      this._mapService.releaseMapClick(PlotService.CLICK_OWNER_ID);
    });
  }

  /**
   * method to start the selection of the area for plotting
   * @param settings various settings used for different types of drawing
   */
  public selectArea(settings: { ratio?: number; scale?: number; width?: number; height?: number }) {
    // prepare the drawing object
    if (settings.ratio) {
      this._drawObject = new Draw({
        source: this._temporaryDrawingSource,
        type: 'Circle',
        geometryFunction: this._createLimitedBox(settings.ratio)
      });
    } else {
      this._drawObject = new Draw({
        source: this._temporaryDrawingSource,
        type: 'Point',
        geometryFunction: this._createBoundingBox(settings.scale, settings.width, settings.height)
      });
    }

    // listen to the drawend event and clear the previous features
    this._drawObject.on('drawend', () => {
      this.discardSelection();
      this._isLayerDrawnSubject.next(true);
    });

    // add the drawing interaction to the openlayers map
    this._mapService.openLayersMap.addInteraction(this._drawObject);

    // remove the backdrop and the menu
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    backdrop.classList.toggle('disappear');
    this._dialogRef.addPanelClass('disappear');

    // open the snackbar to allow the user to interact with the selection
    // pinned: se un altro messaggio (es. "strumento già aperto") la sostituisce, torna alla sua chiusura
    this._snackbarRef = this._snackbar.openPinned(PlotMapSnackbarComponent, {
      panelClass: ['custom-snackbar']
    });
  }

  public isPlotOpen(): boolean {
    return this._dialogRef || this._snackbarRef ? true : false;
  }

  public acceptSelection(): void {
    this._mapService.openLayersMap.removeInteraction(this._drawObject);
    this._snackbarRef.dismiss();
    this._dialogRef.removePanelClass('disappear');
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    backdrop.classList.remove('disappear');
  }

  public discardSelection(): void {
    // Rimuovere il disegno dal layer
    this._temporaryDrawingSource.clear();
    this._isLayerDrawnSubject.next(false);
  }

  public cancelSelection(): void {
    this._mapService.openLayersMap.removeInteraction(this._drawObject);
    this._snackbarRef.dismiss();
    this._dialogRef.removePanelClass('disappear');
    const backdrop = document.querySelector('.cdk-overlay-backdrop');
    backdrop.classList.remove('disappear');
    this._temporaryDrawingSource.clear();
    this._isLayerDrawnSubject.next(false);
  }

  public save(pdfSettings: any, template: any): void {
    let activeBaseLayers = [];

    for (let [_, value] of this._mapService.activeLayers.entries()) {
      if (value.get(MapService.GROUP_LAYER_KEY) == 'MAPSERVICE' && value.getProperties().visible == true) {
        activeBaseLayers.push(value.get(MapService.LABEL_LAYER_KEY));
      }
    }

    let plotUrl = this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PLOT);

    let propertiesCount = 0;
    let attributes = [];

    for (let property in pdfSettings) {
      if (template.attributes[property]) {
        attributes.push(property);
        continue;
      }

      plotUrl += `${propertiesCount == 0 ? '?' : '&'}${property}=${pdfSettings[property]}`;
      propertiesCount++;
    }

    let attributesObject = {};

    for (let attribute of attributes) {
      attributesObject[attribute] = pdfSettings[attribute];
    }

    plotUrl += `&attributes=${safeJsonStringify(attributesObject) ?? ''}`;

    if (this._temporaryDrawingSource.getFeatures().length == 0) {
      this._ebwModalSrv.openErrorModal({
        title: this._i18nService.getInstantValue('common.error'),
        content: this._i18nService.getInstantValue('map.plotting.error.missing_geometry')
      });

      return;
    }

    if (activeBaseLayers.length == 0) {
      this._ebwModalSrv.openErrorModal({
        title: this._i18nService.getInstantValue('common.error'),
        content: this._i18nService.getInstantValue('map.plotting.error.missing_layers')
      });

      return;
    }

    let extent = this._temporaryDrawingSource.getFeatures()[0].getGeometry().getExtent();

    if (pdfSettings.view_scale) {
      let center = [Math.abs(extent[0] + extent[2]) / 2, Math.abs(extent[1] + extent[3]) / 2];
      plotUrl += `&centre=${center[0]},${center[1]}`;
    } else {
      plotUrl += `&bounds=${extent[0]},${extent[1]},${extent[2]},${extent[3]}`;
    }
    plotUrl += '&crs=EPSG:3857';
    plotUrl += `&layers=${activeBaseLayers.join(',')}`;

    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this._loginService.getToken()
    });

    const opt = { headers: headers };

    let user_id = sessionStorage.getItem('user_id');

    if (user_id) {
      opt.headers = opt.headers.append('user', user_id);
    }

    this._http.get(plotUrl, opt).subscribe(
      (response: any) => {
        this._ebwModalSrv.openSuccessModal({
          title: this._i18nService.getInstantValue('map.plotting.plotting_success'),
          content: this._i18nService.getInstantValue('map.plotting.plotting_success_description')
        });

        this._dialogRef.close();

        // Aggiorna lo stato di disegno
        this._isLayerDrawnSubject.next(false);
      },
      (error) => {
        console.error('[PlotService][save]', error);
        this._ebwModalSrv.openErrorModal({
          title: this._i18nService.getInstantValue('common.error'),
          content: this._i18nService.getInstantValue('map.plotting.plotting_error')
        });
      }
    );
  }

  public getTemplatesPlotUrl(): Observable<any> {
    // chiamata HTTP per il fetch dei valori dei template. aggiungendo '?error=1' al getTemplatesUrl, si può testare un caso di errore mock

    let noInterceptHttpClient = new HttpClient(this._handler);

    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
    });

    const opt = { headers: headers };

    return noInterceptHttpClient.get(this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.PLOT_TEMPLATES), opt).pipe(
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  /**
   * function to create a geometry function for a rectangle with a fixed ratio
   * @param ratio ratio between the 2 sides of the rectangle
   * @returns geometry function that creates a rectangle with a fixed ratio
   */
  private _createLimitedBox(ratio: number) {
    // return the geometry function which takes in the current coordinates and the current geometry
    return function (coordinates: number[][], geometry: SimpleGeometry): SimpleGeometry {
      // setup the constants to keep track of the X and Y coordinate indices
      const X: number = 0;
      const Y: number = 1;

      // extract the vertices of the selection
      let topLeft: number[] = [coordinates[0][X], coordinates[0][Y]];
      let bottomRight: number[] = [coordinates[1][X], coordinates[1][Y]];

      // calculate the width and height of the rectangle between the starting point and the cursor
      let selectionWidth: number = Math.abs(bottomRight[X] - topLeft[X]);
      let selectionHeight: number = Math.abs(topLeft[Y] - bottomRight[Y]);

      // calculate the width and height of the box inscribed in the selection rectangle that has the correct ratio
      let boxWidth: number = 0;
      let boxHeight: number = 0;
      // if the selection is too tall compared to the ratio
      if (selectionWidth / ratio <= selectionHeight) {
        // take the current selection width
        boxWidth = selectionWidth;
        // calculate the height with the ratio
        boxHeight = selectionWidth / ratio;
      }
      // if the selection is too wide compared to the ratio
      else if (selectionHeight * ratio <= selectionWidth) {
        // calculate the width with the ratio
        boxWidth = selectionHeight * ratio;
        // take the current selection height
        boxHeight = selectionHeight;
      }

      // flip the width and height depending on the position of the cursor compared to the first point
      if (coordinates[0][X] > coordinates[1][X]) {
        boxWidth = -boxWidth;
      }
      if (coordinates[0][Y] < coordinates[1][Y]) {
        boxHeight = -boxHeight;
      }

      // calculate the coordinates of the new geometry by starting from the first point and adding the calculated dimensions
      let boxCoordinates: number[][][] = [
        [topLeft, [topLeft[X] + boxWidth, topLeft[Y]], [topLeft[X] + boxWidth, topLeft[Y] - boxHeight], [topLeft[X], topLeft[Y] - boxHeight], topLeft]
      ];

      // if there is already a geometry set, update its coordinates, otherwise create a new geometry
      if (geometry) {
        geometry.setCoordinates(boxCoordinates);
      } else {
        geometry = new Polygon(boxCoordinates);
      }

      // return the update/new geometry
      return geometry;
    };
  }

  /**
   * function to create a geometry function for a rectangle with a fixed ratio
   * @param ratio ratio between the 2 sides of the rectangle
   * @returns geometry function that creates a rectangle with a fixed ratio
   */
  private _createBoundingBox(scale: number, width: number, height: number) {
    // return the geometry function which takes in the current coordinates and the current geometry
    return function (coordinates: number[], geometry: SimpleGeometry): SimpleGeometry {
      // setup the constants to keep track of the X and Y coordinate indices
      const X: number = 0;
      const Y: number = 1;

      const DPIcm = 72; // dots/cm

      let groundWidth = width / DPIcm; // cm
      let groundHeight = height / DPIcm; // cm

      let groundWidthm = groundWidth / 100; // m
      let groundHeightm = groundHeight / 100; // m

      let scaledGroundWidth = groundWidthm * scale; // m
      let scaledGroundHeight = groundHeightm * scale; // m

      let halfScaledGroundWidth = scaledGroundWidth / 2;
      let halfScaledGroundHeight = scaledGroundHeight / 2;

      // return the update/new geometry
      return new Polygon([
        [
          [coordinates[X] + halfScaledGroundWidth, coordinates[Y] + halfScaledGroundHeight],
          [coordinates[X] + halfScaledGroundWidth, coordinates[Y] - halfScaledGroundHeight],
          [coordinates[X] - halfScaledGroundWidth, coordinates[Y] - halfScaledGroundHeight],
          [coordinates[X] - halfScaledGroundWidth, coordinates[Y] + halfScaledGroundHeight],
          [coordinates[X] + halfScaledGroundWidth, coordinates[Y] + halfScaledGroundHeight]
        ]
      ]);
    };
  }
}
